import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { invoiceSchema, type InvoiceFormData } from '@/lib/validators';
import React from 'react';
import { useInvoices, useInvoice } from '@/composables/financial/useInvoices';
import { useContactOptions } from '@/composables/crm/useContactOptions';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { useSignatures } from '@/composables/shared/useSignatures';
import { formatCurrency, formatFullName } from '@/lib/formatters';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Eye, Plus } from 'lucide-react';
import { usePdfActions, PdfAbort } from '@/composables/shared/usePdfActions';
import { invoiceService } from '@/services/invoice.service';
import { LineItemsEditor } from '@/components/forms/LineItemsEditor';
import { GstinWarning } from '@/components/forms/GstinWarning';
import { cleanChildren, itemsToForm } from '@/lib/documentItems';
import { previewTaxLines, taxSupplyOptions } from '@/lib/tax';
import { CURRENCIES } from '@/lib/constants';

export function InvoiceCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const { createInvoice, updateInvoice } = useInvoices();
  // Pickers read the full contact list, never the filtered list store.
  const { contacts } = useContactOptions();
  const { settings: orgSettings } = useOrganizationSettings();
  const { signatures, defaultSignature } = useSignatures();
  const { invoice: existing, isLoading: loadingExisting } = useInvoice(id || '');
  const orgPrefillApplied = React.useRef(false);
  const signaturePrefillApplied = React.useRef(false);
  // Which contact the billing fields were last filled from — see the autofill
  // effect below.
  const autofilledContactId = React.useRef<string | null>(null);
  // Once any payment has been recorded, line items / totals / currency are locked.
  // Notes, terms, billing and payment info can still be edited.
  const isLocked = isEdit && !!existing && (Number(existing.paidAmount) > 0 || existing.status === 'PAID');

  const form = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      issueDate: '', dueDate: '', currency: 'USD', taxRate: 0, discountAmount: 0,
      pricingMode: 'ITEMIZED', subtotal: 0,
      notes: '', terms: '', contactId: '', signatureId: '',
      billingCompanyName: '',
      taxId: '', paymentBankName: '', paymentBankAccountName: '', paymentBankAccountNumber: '',
      paymentBankIfsc: '', paymentBankSwift: '', paymentUpiId: '', paymentPaypalEmail: '', paymentInstructions: '',
      billingTaxId: '', taxSupply: 'AUTO',
      items: [{ description: '', quantity: 1, unitPrice: 0, taxRate: 0, children: [] }],
    },
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });

  // Pre-fill tax ID and payment defaults from organization settings (only once, only if user hasn't typed anything, only on create).
  React.useEffect(() => {
    if (isEdit || !orgSettings || orgPrefillApplied.current) return;
    orgPrefillApplied.current = true;
    const setIfEmpty = (k: keyof InvoiceFormData, v: string | null | undefined) => {
      if (!v) return;
      const current = form.getValues(k);
      if (!current) form.setValue(k, v as never);
    };
    setIfEmpty('taxId', orgSettings.taxId);
    setIfEmpty('paymentBankName', orgSettings.bankName);
    setIfEmpty('paymentBankAccountName', orgSettings.bankAccountName);
    setIfEmpty('paymentBankAccountNumber', orgSettings.bankAccountNumber);
    setIfEmpty('paymentBankIfsc', orgSettings.bankIfsc);
    setIfEmpty('paymentBankSwift', orgSettings.bankSwift);
    setIfEmpty('paymentUpiId', orgSettings.upiId);
    setIfEmpty('paymentPaypalEmail', orgSettings.paypalEmail);
    setIfEmpty('paymentInstructions', orgSettings.paymentInstructions);
  }, [isEdit, orgSettings, form]);

  // Pre-select the default signature (only once, only on create).
  React.useEffect(() => {
    if (isEdit || signaturePrefillApplied.current || !defaultSignature) return;
    if (!form.getValues('signatureId')) {
      form.setValue('signatureId', defaultSignature.id);
      signaturePrefillApplied.current = true;
    }
  }, [isEdit, defaultSignature, form]);

  // Hydrate form when editing an existing invoice
  React.useEffect(() => {
    if (!isEdit || !existing) return;
    form.reset({
      issueDate: (existing.issueDate || '').slice(0, 10),
      dueDate: (existing.dueDate || '').slice(0, 10),
      currency: (existing.currency as InvoiceFormData['currency']) || 'USD',
      taxRate: Number(existing.taxRate) || 0,
      discountAmount: Number(existing.discountAmount) || 0,
      notes: existing.notes ?? '',
      terms: existing.terms ?? '',
      billingCompanyName: existing.billingCompanyName ?? '',
      billingName: existing.billingName ?? '',
      billingEmail: existing.billingEmail ?? '',
      billingAddress: existing.billingAddress ?? '',
      billingCity: existing.billingCity ?? '',
      billingState: existing.billingState ?? '',
      billingCountry: existing.billingCountry ?? '',
      billingPostalCode: existing.billingPostalCode ?? '',
      contactId: existing.contactId ?? '',
      signatureId: existing.signatureId ?? '',
      taxId: existing.taxId ?? '',
      paymentBankName: existing.paymentBankName ?? '',
      paymentBankAccountName: existing.paymentBankAccountName ?? '',
      paymentBankAccountNumber: existing.paymentBankAccountNumber ?? '',
      paymentBankIfsc: existing.paymentBankIfsc ?? '',
      paymentBankSwift: existing.paymentBankSwift ?? '',
      paymentUpiId: existing.paymentUpiId ?? '',
      paymentPaypalEmail: existing.paymentPaypalEmail ?? '',
      paymentInstructions: existing.paymentInstructions ?? '',
      pricingMode: existing.pricingMode ?? 'ITEMIZED',
      subtotal: Number(existing.subtotal) || 0,
      billingTaxId: existing.billingTaxId ?? '',
      taxSupply: existing.taxSupply ?? 'AUTO',
      items: itemsToForm(existing.items),
    });
    // Mark the hydrated contact as already applied, so the autofill effect
    // treats it as the document's own snapshot rather than a fresh pick.
    autofilledContactId.current = existing.contactId ?? '';
  }, [isEdit, existing, form]);

  // Auto-fill billing fields when the user PICKS a contact.
  //
  // The ref is what makes that "the user picks" rather than "the value exists".
  // On an edit page the contacts list resolves after form.reset(), so without
  // it this effect fired on the hydrated value and overwrote the invoice's
  // billing snapshot with the contact's current details — silently renaming a
  // client on an already-issued document. It is seeded during hydration, and
  // only moves once a lookup actually succeeds, so a pick made before the list
  // arrives still fills in when it does.
  const selectedContactId = form.watch('contactId');
  React.useEffect(() => {
    if (!selectedContactId || autofilledContactId.current === selectedContactId) return;
    const contact = contacts.find(c => c.id === selectedContactId);
    if (!contact) return;
    autofilledContactId.current = selectedContactId;
    form.setValue('billingName', `${contact.firstName} ${contact.lastName}`);
    if (contact.company?.name) form.setValue('billingCompanyName', contact.company.name);
    if (contact.email) form.setValue('billingEmail', contact.email);
    if (contact.address) form.setValue('billingAddress', contact.address);
    if (contact.city) form.setValue('billingCity', contact.city);
    if (contact.state) form.setValue('billingState', contact.state);
    if (contact.country) form.setValue('billingCountry', contact.country);
    if (contact.postalCode) form.setValue('billingPostalCode', contact.postalCode);
  }, [selectedContactId, contacts, form]);

  // Live totals (so users see the discount applied immediately)
  const watchItems = form.watch('items');
  const watchTaxRate = form.watch('taxRate');
  const watchDiscount = form.watch('discountAmount');
  const pricingMode = form.watch('pricingMode');
  const watchSubtotal = form.watch('subtotal');
  const watchCurrency = form.watch('currency');
  const money = (v: number) => formatCurrency(v, watchCurrency);
  const lumpSum = pricingMode === 'LUMP_SUM';
  // Lump-sum: the typed subtotal is the truth. Itemized: derive it from the items.
  const subtotalLive = lumpSum
    ? Number(watchSubtotal) || 0
    : (watchItems || []).reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unitPrice) || 0), 0);
  const taxLive = subtotalLive * ((Number(watchTaxRate) || 0) / 100);
  const totalLive = Math.max(0, subtotalLive + taxLive - (Number(watchDiscount) || 0));
  // What the tax will print as. Offered whenever tax is charged (or a choice
  // is already stored): one line by default, CGST + SGST / IGST on request.
  const taxSupply = form.watch('taxSupply');
  const showTaxLines = Number(watchTaxRate) > 0 || taxSupply !== 'AUTO';
  const taxLinesLive = previewTaxLines(Number(watchTaxRate) || 0, taxLive, orgSettings, taxSupply, {
    billingState: form.watch('billingState'), billingCountry: form.watch('billingCountry'),
  });

  // Under lump-sum the items carry no money — the document subtotal does.
  const buildPayload = (data: InvoiceFormData) => {
    const lump = data.pricingMode === 'LUMP_SUM';
    const items = data.items.map((item, i) => ({
      description: item.description,
      quantity: lump ? 0 : item.quantity,
      unitPrice: lump ? 0 : item.unitPrice,
      amount: lump ? 0 : item.quantity * item.unitPrice,
      taxRate: lump ? 0 : item.taxRate,
      position: i,
      children: cleanChildren(item.children),
    }));
    const subtotal = lump ? Number(data.subtotal) || 0 : items.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = subtotal * (data.taxRate / 100);
    const totalAmount = Math.max(0, subtotal + taxAmount - data.discountAmount);
    // AUTO is the form's word for "no override"; the API takes null.
    return { ...data, items, subtotal, taxAmount, totalAmount, taxSupply: data.taxSupply === 'AUTO' ? null : data.taxSupply };
  };

  // Preview the form as it stands — nothing is saved. The tab opens on the
  // click itself (so it isn't blocked), then validation runs. A paid invoice
  // previews with its stored money; the server ignores the locked fields.
  const pdf = usePdfActions();
  const handlePreview = () => pdf.preview(async () => {
    if (!(await form.trigger())) throw new PdfAbort('Fix the highlighted fields to preview.');
    return invoiceService.preview(buildPayload(invoiceSchema.parse(form.getValues())), isEdit ? id : undefined);
  }, 'invoice-preview.pdf');

  const onSubmit = (data: InvoiceFormData) => {
    if (isEdit && id) {
      if (isLocked) {
        // Strip out financial fields — backend will reject them anyway, but
        // sending them risks the API ignoring the whole patch silently.
        const { items: _items, taxRate: _tax, discountAmount: _disc, currency: _cur, pricingMode: _pm, subtotal: _sub, taxSupply: _supply, ...rest } = data;
        updateInvoice.mutate({ id, data: rest }, { onSuccess: () => navigate(`/financial/invoices/${id}`) });
        return;
      }
      updateInvoice.mutate({ id, data: buildPayload(data) }, { onSuccess: () => navigate(`/financial/invoices/${id}`) });
      return;
    }

    createInvoice.mutate(buildPayload(data), { onSuccess: () => navigate('/financial/invoices') });
  };

  if (isEdit && loadingExisting) {
    return <PageContainer backTo title="Edit Invoice"><div className="text-muted-foreground text-sm">Loading…</div></PageContainer>;
  }

  const submitting = createInvoice.isPending || updateInvoice.isPending;

  return (
    <PageContainer backTo title={isEdit ? 'Edit Invoice' : 'Create Invoice'}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {isLocked && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            This invoice has payments recorded against it. Line items, totals, tax and currency are locked. You can still update notes, terms, billing info and payment details.
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card><CardHeader><CardTitle className="text-base">Invoice Details</CardTitle></CardHeader><CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4"><FormDatePicker form={form} name="issueDate" label="Issue Date" required /><FormDatePicker form={form} name="dueDate" label="Due Date" required /></div>
            <FormSelect form={form} name="currency" label="Currency" options={CURRENCIES.map(c => ({ label: c, value: c }))} disabled={isLocked} />
            <div className="grid grid-cols-2 gap-4">
              <FormField form={form} name="taxRate" label="Tax Rate (%)" type="decimal" disabled={isLocked} />
              <FormField form={form} name="discountAmount" label="Discount Amount" type="decimal" description="Deducted from total" disabled={isLocked} />
            </div>
            <FormField form={form} name="taxId" label="Tax ID / GSTIN" description="Pre-filled from settings — edit if this invoice differs" />
            {showTaxLines && (
              <FormSelect form={form} name="taxSupply" label="Tax lines" options={taxSupplyOptions(orgSettings?.taxMode, orgSettings?.taxLabel)} disabled={isLocked} />
            )}
            <GstinWarning taxRate={Number(watchTaxRate) || 0} docTaxId={form.watch('taxId')} orgTaxId={orgSettings?.taxId} />
            <FormSelect
              form={form}
              name="signatureId"
              label="Signature"
              options={signatures.map((s) => ({ label: `${s.label}${s.isDefault ? ' (Default)' : ''}`, value: s.id }))}
              placeholder={defaultSignature ? `Organisation default — ${defaultSignature.label}` : 'Select…'}
            />
          </CardContent></Card>
          <Card><CardHeader><CardTitle className="text-base">Billing Info</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormSelect form={form} name="contactId" label="Contact" options={contacts.map(c => ({ label: formatFullName(c.firstName, c.lastName), value: c.id }))} />
            <FormField form={form} name="billingCompanyName" label="Client Company" description="Shown on top of the BILL TO block (optional)" />
            <FormField form={form} name="billingName" label="Client Name" /><FormField form={form} name="billingEmail" label="Client Email" type="email" /><FormField form={form} name="billingAddress" label="Address" />
            <div className="grid grid-cols-2 gap-4"><FormField form={form} name="billingCity" label="City" /><FormField form={form} name="billingState" label="State" /></div>
            <div className="grid grid-cols-2 gap-4">
              <FormField form={form} name="billingCountry" label="Country" />
              <FormField form={form} name="billingPostalCode" label="PIN / Postal Code" />
            </div>
            <FormField form={form} name="billingTaxId" label="Client GSTIN / Tax ID" description="The client's own registration, printed under their address" />
          </CardContent></Card>
        </div>

        <Card className="mt-6">
          <CardHeader className="space-y-3">
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Line Items</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => append({ description: '', quantity: 1, unitPrice: 0, taxRate: 0, children: [] })} disabled={isLocked}><Plus className="mr-1 h-4 w-4" />Add Item</Button>
            </div>
            <RadioGroup
              value={pricingMode}
              onValueChange={(v) => form.setValue('pricingMode', v as 'ITEMIZED' | 'LUMP_SUM')}
              className="flex flex-wrap gap-x-6 gap-y-2"
              disabled={isLocked}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="ITEMIZED" id="pm-itemized" />
                <Label htmlFor="pm-itemized" className="text-sm font-normal">Price each item</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="LUMP_SUM" id="pm-lump" />
                <Label htmlFor="pm-lump" className="text-sm font-normal">List items, one total</Label>
              </div>
            </RadioGroup>
            <CardDescription>
              {lumpSum
                ? 'Items are listed by description only. Qty and unit price are hidden on the invoice PDF.'
                : 'The subtotal is calculated from quantity × unit price.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
          <LineItemsEditor form={form} fields={fields} remove={remove} lumpSum={lumpSum} disabled={isLocked} />
          {form.formState.errors.items && <p className="text-xs text-destructive">{form.formState.errors.items.message || form.formState.errors.items.root?.message}</p>}

          {lumpSum && (
            <div className="border-t pt-4 mt-4 ml-auto w-full max-w-xs">
              <Label htmlFor="lump-subtotal">Total for all items</Label>
              <DecimalInput id="lump-subtotal" {...form.register('subtotal')} disabled={isLocked} />
              <p className="mt-1 text-xs text-muted-foreground">Before tax and discount.</p>
              {form.formState.errors.subtotal && <p className="mt-1 text-xs text-destructive">{form.formState.errors.subtotal.message}</p>}
            </div>
          )}

          {/* Live totals */}
          <div className="border-t pt-4 mt-4 ml-auto w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(subtotalLive)}</span></div>
            {taxLinesLive.map((line) => (
              <div key={line.label} className="flex justify-between"><span className="text-muted-foreground">{line.label}</span><span>{money(line.amount)}</span></div>
            ))}
            {Number(watchDiscount) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>- {money(Number(watchDiscount))}</span></div>}
            <div className="flex justify-between font-bold pt-1 border-t"><span>Total</span><span>{money(totalLive)}</span></div>
          </div>
        </CardContent></Card>

        <Card className="mt-6">
          <CardHeader><CardTitle className="text-base">Payment Info</CardTitle><CardDescription>Pre-filled from organization settings — override per-invoice if needed.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField form={form} name="paymentBankName" label="Bank Name" />
              <FormField form={form} name="paymentBankAccountName" label="Account Holder" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField form={form} name="paymentBankAccountNumber" label="Account Number" />
              <FormField form={form} name="paymentBankIfsc" label="IFSC" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField form={form} name="paymentBankSwift" label="SWIFT" />
              <FormField form={form} name="paymentUpiId" label="UPI ID" />
            </div>
            <FormField form={form} name="paymentPaypalEmail" label="PayPal Email" type="email" />
            <FormTextarea form={form} name="paymentInstructions" label="Additional Instructions" />
          </CardContent>
        </Card>

        <Card className="mt-6"><CardHeader><CardTitle className="text-base">Notes</CardTitle></CardHeader><CardContent className="space-y-4">
          <FormTextarea form={form} name="notes" label="Notes" /><FormTextarea form={form} name="terms" label="Terms & Conditions" />
        </CardContent></Card>

        <div className="mt-6 flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="button" variant="outline" onClick={handlePreview} loading={pdf.isPreviewing}><Eye className="mr-2 h-4 w-4" />Preview</Button>
          <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Invoice'}</Button>
        </div>
      </form>
    </PageContainer>
  );
}
